import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewEncapsulation,
} from '@angular/core';
import { IOwnershipUser } from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership';
import { ObjectTypes } from '@fi-shared/types';
import { FiTransferOwnershipHelperService } from '@fi-app/other-settings/transfer-ownership/fi-transfer-ownership-helper.service';
import { AppTextService } from '@fi-shared/app-text';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'fi-transfer-ownership-summary-card',
    templateUrl: './fi-transfer-ownership-summary-card.component.html',
    styleUrl: './fi-transfer-ownership-summary-card.component.scss',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-transfer-ownership-summary-card' },
    standalone: false,
})
export class FiTransferOwnershipSummaryCardComponent implements OnInit, OnDestroy {
    @Input({ required: true }) sourceUser: IOwnershipUser;
    @Input({ required: true }) targetUser: IOwnershipUser;
    @Input({ required: true }) selectedObjectTypes: ObjectTypes[];
    @Input({ required: true }) sourceUserAreaName: string;
    @Input({ required: true }) targetUserAreaName: string;
    @Input() isShowSummaryButtonDisabled: boolean;
    @Input() resetSummary$ = new Observable<void>();
    @Input() isDisabled: boolean;
    @Output() onTransferOwnershipConfirm = new EventEmitter<void>();

    protected isSummaryStep: boolean;
    protected signText: string;
    private ngOnDestroy$ = new Subject<void>();

    constructor(
        private fiTransferOwnershipHelperService: FiTransferOwnershipHelperService,
        private appTextService: AppTextService,
    ) {}

    public ngOnInit(): void {
        this.resetSummary$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.isSummaryStep = false;
            this.signText = undefined;
        });
    }

    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected showSummary(): void {
        this.isSummaryStep = true;
    }

    protected getSummaryDescription(): string {
        return this.appTextService.get('ownership.summary_section_description', {
            '{0}': this.sourceUser?.FullName,
            '{1}': this.targetUser?.FullName,
        });
    }

    protected isProceedButtonDisabled(): boolean {
        return !this.signText?.trim()?.length || this.isDisabled;
    }

    protected onTransferOwnershipClick(): void {
        this.fiTransferOwnershipHelperService
            .showTransferOwnershipConfirmationDialog({
                transferredItemsTotal: this.getTransferredItemsTotalCount(),
                sourceUserName: this.sourceUser.FullName,
                targetUserName: this.targetUser.FullName,
            })
            .onYesButtonClick(() => this.onTransferOwnershipConfirm.emit());
    }

    private getTransferredItemsTotalCount(): number {
        return this.selectedObjectTypes.reduce(
            (total, objectType) => total + (this.sourceUser.Ownership[objectType]?.length ?? 0),
            0,
        );
    }
}
